import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { calcularScoreGUT, getGUTPrioridade, GUT_LABELS } from '@/hooks/useAvaliacaoGUT';

interface GUTScoreDisplayProps {
  gravidade?: number;
  urgencia?: number;
  tendencia?: number;
  score?: number;
  prioridade?: string;
  variant?: 'compact' | 'full';
}

const getPrioridadeColor = (prioridade: string) => {
  switch (prioridade) {
    case 'critica':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'alta':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'media':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'baixa':
      return 'bg-green-100 text-green-700 border-green-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const getPrioridadeLabel = (prioridade: string) => {
  switch (prioridade) {
    case 'critica':
      return 'Crítica';
    case 'alta':
      return 'Alta';
    case 'media':
      return 'Média';
    case 'baixa':
      return 'Baixa';
    default:
      return '-';
  }
};

export const GUTScoreDisplay = ({
  gravidade,
  urgencia,
  tendencia,
  score: propScore,
  prioridade: propPrioridade,
  variant = 'full'
}: GUTScoreDisplayProps) => {
  // Calculate score if not provided
  const calculatedScore = propScore ?? (gravidade && urgencia && tendencia 
    ? calcularScoreGUT(gravidade, urgencia, tendencia) 
    : 0);
  
  const calculatedPrioridade = propPrioridade ?? getGUTPrioridade(calculatedScore);

  if (!gravidade && !urgencia && !tendencia && !propScore) {
    return (
      <div className="text-sm text-muted-foreground">
        Avaliação GUT não realizada
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className={cn('cursor-help', getPrioridadeColor(calculatedPrioridade))}
            >
              GUT: {calculatedScore}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs space-y-1">
              <p>G: {gravidade || '-'} × U: {urgencia || '-'} × T: {tendencia || '-'}</p>
              <p>Prioridade: {getPrioridadeLabel(calculatedPrioridade)}</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-4 bg-muted/30 rounded-lg border">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Score GUT</span>
        <span className="text-2xl font-bold">{calculatedScore}</span>
      </div>
      
      <Badge 
        variant="outline" 
        className={cn('w-fit', getPrioridadeColor(calculatedPrioridade))}
      >
        {getPrioridadeLabel(calculatedPrioridade)}
      </Badge>
      
      {gravidade && urgencia && tendencia && (
        <div className="text-xs text-muted-foreground mt-1">
          G:{gravidade} × U:{urgencia} × T:{tendencia} = {calculatedScore}
        </div>
      )}
    </div>
  );
};

export default GUTScoreDisplay;
